import { useCallback, useEffect, useState } from 'react';
import type {
  ConsumerWireInstructionsModel,
  ExternalPaymentMethod,
} from './failed-payments.types';

import { areExternalPaymentInstructionsValid } from './failed-payments-utils';

type ApiClient = {
  get: <T>(url: string) => Promise<T>;
};

export function useWireInstructions({
  apiClient,
  enabled,
  homeUniqueId,
  method,
  balanceUniqueIds,
}: {
  apiClient: ApiClient;
  enabled: boolean;
  homeUniqueId?: string | null;
  method: ExternalPaymentMethod | null;
  balanceUniqueIds: string[];
}) {
  const query = new URLSearchParams();
  if (homeUniqueId) query.set('homeUniqueId', homeUniqueId);
  query.set('method', method === 'ZelleTransfer' ? 'Zelle' : 'Wire');
  for (const id of balanceUniqueIds) query.append('balanceUniqueIds', id);
  const requestKey =
    enabled && method && balanceUniqueIds.length ? query.toString() : null;
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const [result, setResult] = useState<{
    key: string;
    instructions: ConsumerWireInstructionsModel | null;
    error: boolean;
  } | null>(null);

  useEffect(() => {
    if (!requestKey) {
      setResult(null);
      return;
    }
    let cancelled = false;
    setResult(null);
    apiClient
      .get<ConsumerWireInstructionsModel>(
        `/v2/users/failed-payments/wire-instructions?${requestKey}`,
      )
      .then(
        (instructions) => {
          const valid =
            method && areExternalPaymentInstructionsValid(instructions, method);
          if (!cancelled)
            setResult({
              key: requestKey,
              instructions: valid ? instructions : null,
              error: !valid,
            });
        },
        () => {
          if (!cancelled)
            setResult({ key: requestKey, instructions: null, error: true });
        },
      );
    return () => {
      cancelled = true;
    };
  }, [apiClient, attempt, method, requestKey]);

  const current = result?.key === requestKey ? result : null;
  return {
    instructions: current?.instructions ?? null,
    loading: Boolean(requestKey && !current),
    error: current?.error ?? false,
    retry,
  };
}
